import { useRef, useState, useEffect, useMemo } from "react";
import { api, formatApiError, API_BASE } from "../lib/api";
import {
  Calculator, FileDown, Save, Briefcase, Ruler, DollarSign, Tag, Layers,
  TrendingUp, History, RotateCcw, Trash2, FileSpreadsheet, AlertCircle, Clock, Wallet, Package,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

function fmtNum(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (Math.abs(n) >= 1000) return (n / 1000).toFixed(2) + "K";
  return Number(n).toLocaleString();
}
function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const EMPTY_FORM = {
  job_name: "",
  estimated_sqft: "",
  budgeted_labor_cost: "",
  budgeted_material_cost: "",
  phase: "",
  category: "",
};

export default function JobForecasting() {
  const [tab, setTab] = useState("new");
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  const [catFilter, setCatFilter] = useState("");
  const [options, setOptions] = useState({ phases: [], categories: [], by_phase: {} });
  const reportRef = useRef(null);

  const totalBudget = useMemo(() => {
    const l = parseFloat(form.budgeted_labor_cost) || 0;
    const m = parseFloat(form.budgeted_material_cost) || 0;
    return l + m;
  }, [form.budgeted_labor_cost, form.budgeted_material_cost]);

  const loadSaved = async () => {
    try { const { data } = await api.get("/forecasting/saved"); setSaved(data); } catch { /* noop */ }
  };
  const loadOptions = async () => {
    try { const { data } = await api.get("/forecasting/options"); setOptions(data); } catch { /* noop */ }
  };
  useEffect(() => { loadSaved(); loadOptions(); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const clearAll = () => { setForm(EMPTY_FORM); setResult(null); };

  const submit = async (e) => {
    e.preventDefault();
    const sqft = parseFloat(form.estimated_sqft);
    const labor = parseFloat(form.budgeted_labor_cost) || 0;
    const material = parseFloat(form.budgeted_material_cost) || 0;
    if (!form.job_name.trim()) return toast.error("Job name required");
    if (!sqft || sqft <= 0) return toast.error("Estimated SQFT must be > 0");
    if (labor + material <= 0) return toast.error("Enter a labor or material budget");
    // Phase & Category are OPTIONAL: leaving both blank runs the "job-only"
    // forecast that averages the full historical corpus and surfaces a
    // phase + category breakdown of the comparable work.

    setLoading(true);
    try {
      const { data } = await api.post("/forecasting/run", {
        job_name: form.job_name.trim(),
        estimated_sqft: sqft,
        budgeted_labor_cost: labor,
        budgeted_material_cost: material,
        phase: form.phase.trim(),
        category: form.category.trim(),
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const saveEstimate = async () => {
    if (!result) return;
    try {
      await api.post("/forecasting/save", {
        job_name: result.inputs.job_name,
        estimated_sqft: result.inputs.estimated_sqft,
        budgeted_labor_cost: result.inputs.budgeted_labor_cost,
        budgeted_material_cost: result.inputs.budgeted_material_cost,
        budgeted_cost: result.inputs.total_budgeted_cost,
        phase: result.inputs.phase,
        category: result.inputs.category,
        forecasted_hours: result.forecasted_hours,
        forecasted_labor_cost: result.forecasted_labor_cost,
        forecasted_material_cost: result.forecasted_material_cost,
        forecasted_total_cost: result.forecasted_total_cost,
        labor_variance: result.labor_variance,
        material_variance: result.material_variance,
        predicted_cost: result.forecasted_total_cost,
        variance: result.total_variance,
        similar_jobs_count: result.benchmarks.similar_jobs_count,
      });
      toast.success("Forecast saved");
      loadSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const exportExcel = async () => {
    try {
      const token = localStorage.getItem("mfg_token");
      const res = await fetch(`${API_BASE}/forecasting/export-excel`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) { toast.error("No saved estimates to export"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Forecast_Estimates_${Date.now()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteEstimate = async (id) => {
    if (!window.confirm("Delete this saved estimate?")) return;
    try {
      await api.delete(`/forecasting/saved/${id}`);
      toast.success("Estimate deleted");
      loadSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportPdf = async () => {
    if (!reportRef.current || !result) return;
    toast.info("Rendering PDF…");
    const canvas = await html2canvas(reportRef.current, { backgroundColor: "#0A0B10", scale: 2, useCORS: true });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const ratio = pageW / canvas.width;
    pdf.addImage(img, "PNG", 0, 20, pageW, canvas.height * ratio);
    pdf.save(`Forecast_${result.inputs.job_name.replace(/\s+/g, "_")}.pdf`);
    toast.success("PDF downloaded");
  };

  const categories = useMemo(() => {
    const set = new Set(saved.map((s) => s.category).filter(Boolean));
    return Array.from(set).sort();
  }, [saved]);
  const filteredSaved = catFilter
    ? saved.filter((s) => (s.category || "").toLowerCase() === catFilter.toLowerCase())
    : saved;

  const inputCls = "w-full bg-[#0A0B10] border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#00D4FF] transition-colors";

  return (
    <div className="space-y-6" data-testid="forecasting-page">
      <header>
        <div className="text-[11px] tracking-[0.35em] uppercase text-[#00D4FF]">TOOL · Forecast</div>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-2">Job Forecasting Tool</h1>
        <p className="text-sm text-muted-foreground mt-1">Forecast hours, labor &amp; material costs against historical Phase + Category benchmarks.</p>
      </header>

      <div className="flex border-b border-border" data-testid="forecast-tabs">
        {[
          { id: "new", label: "New Estimate" },
          { id: "category", label: "Category View" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-xs tracking-widest uppercase border-b-2 -mb-px transition-colors
              ${tab === t.id ? "border-[#00D4FF] text-white" : "border-transparent text-muted-foreground hover:text-white"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "new" && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Inputs */}
            <section className="lg:col-span-1 bg-[#12141D] border border-border p-6" data-testid="forecast-inputs">
              <div className="flex items-center gap-2 mb-5">
                <Calculator className="text-[#00D4FF]" size={18} />
                <h2 className="font-heading font-semibold">New Job Estimate</h2>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><Briefcase size={11}/> Job Name</label>
                  <input data-testid="forecast-job-name" value={form.job_name} onChange={(e) => setField("job_name", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><Ruler size={11}/> Estimated SQFT</label>
                  <input data-testid="forecast-sqft" type="number" min="0" step="any" value={form.estimated_sqft} onChange={(e) => setField("estimated_sqft", e.target.value)} className={`${inputCls} font-mono`} />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><DollarSign size={11}/> Budgeted Labor Cost</label>
                  <input data-testid="forecast-budget-labor" type="number" min="0" step="any" value={form.budgeted_labor_cost} onChange={(e) => setField("budgeted_labor_cost", e.target.value)} className={`${inputCls} font-mono`} />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><Package size={11}/> Budgeted Material Cost</label>
                  <input data-testid="forecast-budget-material" type="number" min="0" step="any" value={form.budgeted_material_cost} onChange={(e) => setField("budgeted_material_cost", e.target.value)} className={`${inputCls} font-mono`} />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2"><Wallet size={11}/> Total Budgeted Cost</label>
                  <input
                    data-testid="forecast-budget-total"
                    type="text"
                    readOnly
                    value={fmtMoney(totalBudget)}
                    className={`${inputCls} font-mono bg-[#1A1C28] text-[#00D4FF] cursor-not-allowed`}
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
                    <Layers size={11}/> Phase <span className="text-[9px] text-muted-foreground/70 normal-case tracking-normal">(optional)</span>
                  </label>
                  <select
                    data-testid="forecast-phase"
                    value={form.phase}
                    onChange={(e) => {
                      const newPhase = e.target.value;
                      setForm((f) => ({ ...f, phase: newPhase, category: "" }));
                    }}
                    className={`${inputCls} appearance-none cursor-pointer`}
                  >
                    <option value="">Any / All Phases</option>
                    {options.phases.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
                    <Tag size={11}/> Category <span className="text-[9px] text-muted-foreground/70 normal-case tracking-normal">(optional)</span>
                  </label>
                  <select
                    data-testid="forecast-category"
                    value={form.category}
                    onChange={(e) => setField("category", e.target.value)}
                    className={`${inputCls} appearance-none cursor-pointer`}
                  >
                    <option value="">Any / All Categories</option>
                    {(form.phase && options.by_phase[form.phase]?.length
                      ? options.by_phase[form.phase]
                      : options.categories
                    ).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3 pt-2">
                  <button data-testid="forecast-submit" type="submit" disabled={loading}
                    className="flex-1 bg-[#00D4FF] text-[#0A0B10] font-semibold text-sm tracking-widest uppercase py-3 hover:bg-[#00B8D4] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                    <TrendingUp size={14}/> {loading ? "Forecasting…" : "Forecast →"}
                  </button>
                  <button data-testid="forecast-clear" type="button" onClick={clearAll}
                    className="px-4 bg-transparent border border-border hover:border-[#00D4FF] text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors">
                    <RotateCcw size={12}/> Clear
                  </button>
                </div>
              </form>
            </section>

            {/* Results */}
            <section className="lg:col-span-2" ref={reportRef}>
              {!result && (
                <div className="bg-[#12141D] border border-dashed border-border p-10 text-center text-muted-foreground text-xs tracking-widest uppercase h-full flex items-center justify-center">
                  Enter job details and click Forecast
                </div>
              )}
              {result && (
                <div className="space-y-5">
                  <div className="bg-[#12141D] border border-border p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Forecast for</div>
                        <div className="font-heading font-bold text-2xl mt-1">{result.inputs.job_name}</div>
                        <div className="text-xs text-muted-foreground mt-1 font-mono">
                          Phase: {result.inputs.phase || "Any"} · Category: {result.inputs.category || "Any"} · {fmtNum(result.inputs.estimated_sqft)} SF
                        </div>
                      </div>
                      <span className="text-[10px] tracking-[0.3em] uppercase text-[#00D4FF] whitespace-nowrap">
                        Based on {result.benchmarks.similar_jobs_count} past jobs
                      </span>
                    </div>
                    {result.scope_note && (
                      <div className="mt-3 flex items-start gap-2 text-xs text-amber-300 bg-amber-500/5 border border-amber-500/30 px-3 py-2" data-testid="scope-note">
                        <AlertCircle size={14} className="mt-0.5 shrink-0" />
                        <span>{result.scope_note}</span>
                      </div>
                    )}
                  </div>

                  {/* 7 result cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <ResultCard testId="card-forecasted-hours" icon={<Clock size={14} />} label="Forecasted Hours"
                                value={`${fmtNum(result.forecasted_hours)} hrs`}
                                sub={`@ ${result.benchmarks.avg_hours_per_sqft} hrs/SF`} accent="cyan" />
                    <ResultCard testId="card-forecasted-labor" icon={<DollarSign size={14} />} label="Forecasted Labor Cost"
                                value={fmtMoney(result.forecasted_labor_cost)}
                                sub={`@ ${fmtMoney(result.benchmarks.avg_labor_cost_per_sqft)}/SF`} accent="cyan" />
                    <ResultCard testId="card-forecasted-material" icon={<Package size={14} />} label="Forecasted Material Cost"
                                value={fmtMoney(result.forecasted_material_cost)}
                                sub={`@ ${fmtMoney(result.benchmarks.avg_material_cost_per_sqft)}/SF`} accent="cyan" />
                    <ResultCard testId="card-forecasted-total" icon={<Wallet size={14} />} label="Forecasted Total Cost"
                                value={fmtMoney(result.forecasted_total_cost)}
                                sub={`@ ${fmtMoney(result.benchmarks.avg_total_cost_per_sqft)}/SF`} accent="cyan" />
                    <VarianceCard testId="card-labor-variance" label="Labor Budget Variance"
                                  value={result.labor_variance} status={result.labor_variance_status} />
                    <VarianceCard testId="card-material-variance" label="Material Budget Variance"
                                  value={result.material_variance} status={result.material_variance_status} />
                    <div className="bg-[#12141D] border border-border p-5 sm:col-span-2 lg:col-span-3" data-testid="card-benchmarks">
                      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
                        <TrendingUp size={12} /> Benchmarks from Similar Jobs
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                        <Bench label="Avg Labor / SF" value={fmtMoney(result.benchmarks.avg_labor_cost_per_sqft)} />
                        <Bench label="Avg Material / SF" value={fmtMoney(result.benchmarks.avg_material_cost_per_sqft)} />
                        <Bench label="Avg Total / SF" value={fmtMoney(result.benchmarks.avg_total_cost_per_sqft)} />
                        <Bench label="Avg Hours / SF" value={result.benchmarks.avg_hours_per_sqft} />
                        <Bench label="Jobs Sampled" value={result.benchmarks.similar_jobs_count} highlight />
                      </div>
                    </div>
                  </div>

                  {/* Similar past jobs table */}
                  <div className="bg-[#12141D] border border-border overflow-x-auto">
                    <div className="p-5 pb-3">
                      <h3 className="font-heading font-semibold">Similar Past Jobs</h3>
                      <p className="text-xs text-muted-foreground">Top 5 by SQFT proximity</p>
                    </div>
                    <table className="w-full text-left text-sm" data-testid="similar-jobs-table">
                      <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28]">
                        <tr>
                          <th className="py-3 px-4">Job Name</th>
                          <th className="py-3 px-4">Phase</th>
                          <th className="py-3 px-4">Category</th>
                          <th className="py-3 px-4 text-right">SQFT</th>
                          <th className="py-3 px-4 text-right">Labor $</th>
                          <th className="py-3 px-4 text-right">Material $</th>
                          <th className="py-3 px-4 text-right">Total $</th>
                          <th className="py-3 px-4 text-right">Hours</th>
                          <th className="py-3 px-4 text-right">Lab/SF</th>
                          <th className="py-3 px-4 text-right">Mat/SF</th>
                          <th className="py-3 px-4 text-right">Hrs/SF</th>
                          <th className="py-3 px-4 text-right">Variance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.similar_jobs.map((j, idx) => (
                          <tr key={idx} className="border-t border-border">
                            <td className="py-3 px-4 font-mono text-[#00D4FF]">{j.job}</td>
                            <td className="py-3 px-4 text-xs">{j.phase || "—"}</td>
                            <td className="py-3 px-4 text-xs">{j.category || "—"}</td>
                            <td className="py-3 px-4 font-mono text-right">{fmtNum(j.sqft)}</td>
                            <td className="py-3 px-4 font-mono text-right">{fmtMoney(j.budgeted_labor_cost)}</td>
                            <td className="py-3 px-4 font-mono text-right">{fmtMoney(j.budgeted_material_cost)}</td>
                            <td className="py-3 px-4 font-mono text-right">{fmtMoney(j.total_budgeted_cost)}</td>
                            <td className="py-3 px-4 font-mono text-right">{fmtNum(j.hours)}</td>
                            <td className="py-3 px-4 font-mono text-right">{fmtMoney(j.labor_cost_per_sqft)}</td>
                            <td className="py-3 px-4 font-mono text-right">{fmtMoney(j.material_cost_per_sqft)}</td>
                            <td className="py-3 px-4 font-mono text-right">{j.hours_per_sqft}</td>
                            <td className={`py-3 px-4 font-mono text-right ${j.variance >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                              {j.variance >= 0 ? "+" : ""}{fmtMoney(j.variance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Phase & Category Breakdown — surfaces the shape of the
                       comparable historical corpus so the estimator can see
                       exactly which phases/categories drive the average. */}
                  {(result.phase_breakdown?.length > 0 || result.category_breakdown?.length > 0) && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                      <BreakdownTable
                        title="Phase Breakdown"
                        colLabel="Phase"
                        keyField="phase"
                        rows={result.phase_breakdown}
                        testId="phase-breakdown"
                        accent="#00D4FF"
                      />
                      <BreakdownTable
                        title="Category Breakdown"
                        colLabel="Category"
                        keyField="category"
                        rows={result.category_breakdown}
                        testId="category-breakdown"
                        accent="#A78BFA"
                      />
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>

          {result && (
            <section className="flex flex-wrap items-center gap-3 pt-2">
              <button data-testid="forecast-export-pdf" onClick={exportPdf}
                className="bg-[#00D4FF] text-[#0A0B10] font-semibold px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:bg-[#00B8D4] transition-colors">
                <FileDown size={14}/> Export to PDF
              </button>
              <button data-testid="forecast-save" onClick={saveEstimate}
                className="bg-transparent border border-border hover:border-[#00D4FF] px-4 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 transition-colors">
                <Save size={14}/> Save Estimate
              </button>
            </section>
          )}

          {/* Saved Estimates */}
          {saved.length > 0 && (
            <section className="bg-[#12141D] border border-border overflow-x-auto" data-testid="saved-estimates">
              <div className="p-5 pb-3 flex items-center gap-2">
                <History className="text-[#00D4FF]" size={16} />
                <h3 className="font-heading font-semibold">Saved Estimates</h3>
                <span className="text-xs text-muted-foreground ml-2">{saved.length}</span>
                <div className="ml-auto">
                  <button data-testid="export-excel-btn" onClick={exportExcel}
                    className="px-3 py-1.5 text-xs uppercase tracking-widest border border-border hover:border-[#00D4FF] inline-flex items-center gap-2 transition-colors">
                    <FileSpreadsheet size={12} /> Export to Excel
                  </button>
                </div>
              </div>
              <table className="w-full text-left text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28]">
                  <tr>
                    <th className="py-3 px-4">Saved</th>
                    <th className="py-3 px-4">Job</th>
                    <th className="py-3 px-4">Phase</th>
                    <th className="py-3 px-4">Category</th>
                    <th className="py-3 px-4 text-right">SQFT</th>
                    <th className="py-3 px-4 text-right">Budget Labor</th>
                    <th className="py-3 px-4 text-right">Budget Material</th>
                    <th className="py-3 px-4 text-right">Forecast Hrs</th>
                    <th className="py-3 px-4 text-right">Forecast Labor</th>
                    <th className="py-3 px-4 text-right">Forecast Material</th>
                    <th className="py-3 px-4 text-right">Total Variance</th>
                    <th className="py-3 px-4 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {saved.map((s) => {
                    const totalVar = (s.labor_variance || 0) + (s.material_variance || 0) || s.variance || 0;
                    return (
                      <tr key={s.id} className="border-t border-border" data-testid={`saved-row-${s.id}`}>
                        <td className="py-3 px-4 text-xs font-mono text-muted-foreground">{new Date(s.saved_at).toLocaleString()}</td>
                        <td className="py-3 px-4 font-mono text-[#00D4FF]">{s.job_name}</td>
                        <td className="py-3 px-4">{s.phase || "—"}</td>
                        <td className="py-3 px-4">{s.category || "—"}</td>
                        <td className="py-3 px-4 font-mono text-right">{fmtNum(s.estimated_sqft)}</td>
                        <td className="py-3 px-4 font-mono text-right">{fmtMoney(s.budgeted_labor_cost || 0)}</td>
                        <td className="py-3 px-4 font-mono text-right">{fmtMoney(s.budgeted_material_cost || 0)}</td>
                        <td className="py-3 px-4 font-mono text-right">{fmtNum(s.forecasted_hours)}</td>
                        <td className="py-3 px-4 font-mono text-right">{fmtMoney(s.forecasted_labor_cost || 0)}</td>
                        <td className="py-3 px-4 font-mono text-right">{fmtMoney(s.forecasted_material_cost || 0)}</td>
                        <td className={`py-3 px-4 font-mono text-right ${totalVar >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                          {totalVar >= 0 ? "+" : ""}{fmtMoney(totalVar)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button data-testid={`delete-saved-${s.id}`}
                            onClick={() => deleteEstimate(s.id)}
                            className="text-muted-foreground hover:text-red-400 transition-colors"
                            title="Delete estimate">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {tab === "category" && (
        <section className="space-y-4" data-testid="category-view">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Filter by category</span>
            <button onClick={() => setCatFilter("")}
              className={`px-3 py-1.5 text-xs tracking-wider uppercase border transition-colors
                ${catFilter === "" ? "bg-[#00D4FF] text-[#0A0B10] border-[#00D4FF]"
                                   : "bg-transparent text-[#94A3B8] border-border hover:border-[#00D4FF]/60 hover:text-white"}`}>
              All
            </button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCatFilter(c)}
                data-testid={`cat-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-1.5 text-xs tracking-wider uppercase border transition-colors
                  ${catFilter === c ? "bg-[#00D4FF] text-[#0A0B10] border-[#00D4FF]"
                                    : "bg-transparent text-[#94A3B8] border-border hover:border-[#00D4FF]/60 hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="bg-[#12141D] border border-border overflow-x-auto" data-testid="category-table">
            {filteredSaved.length === 0 ? (
              <div className="p-10 text-center text-xs uppercase tracking-widest text-muted-foreground">
                No saved estimates {catFilter ? `for category "${catFilter}"` : "yet"}
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28]">
                  <tr>
                    <th className="py-3 px-4">Job Name</th>
                    <th className="py-3 px-4">Phase</th>
                    <th className="py-3 px-4">Category</th>
                    <th className="py-3 px-4 text-right">SQFT</th>
                    <th className="py-3 px-4 text-right">Budget Labor</th>
                    <th className="py-3 px-4 text-right">Budget Material</th>
                    <th className="py-3 px-4 text-right">Forecast Hrs</th>
                    <th className="py-3 px-4 text-right">Forecast Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSaved.map((s) => (
                    <tr key={s.id} className="border-t border-border">
                      <td className="py-3 px-4 font-mono text-[#00D4FF]">{s.job_name}</td>
                      <td className="py-3 px-4">{s.phase || "—"}</td>
                      <td className="py-3 px-4">{s.category || "—"}</td>
                      <td className="py-3 px-4 font-mono text-right">{fmtNum(s.estimated_sqft)}</td>
                      <td className="py-3 px-4 font-mono text-right">{fmtMoney(s.budgeted_labor_cost || 0)}</td>
                      <td className="py-3 px-4 font-mono text-right">{fmtMoney(s.budgeted_material_cost || 0)}</td>
                      <td className="py-3 px-4 font-mono text-right">{fmtNum(s.forecasted_hours)}</td>
                      <td className="py-3 px-4 font-mono text-right">{fmtMoney(s.forecasted_total_cost || s.predicted_cost || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function ResultCard({ testId, icon, label, value, sub, accent }) {
  const border = accent === "cyan" ? "border-[#00D4FF]/40" : "border-border";
  return (
    <div data-testid={testId} className={`bg-[#12141D] border ${border} p-5`}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
        {icon} {label}
      </div>
      <div className="font-heading font-bold text-3xl tabular-nums mt-3">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1 font-mono">{sub}</div>}
      <div className="h-[1px] w-8 bg-[#00D4FF] mt-3" />
    </div>
  );
}

function VarianceCard({ testId, label, value, status }) {
  const cls =
    status === "under"   ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300" :
    status === "over"    ? "border-red-500/40 bg-red-500/5 text-red-300" :
                           "border-amber-500/40 bg-amber-500/5 text-amber-300";
  const sublabel =
    status === "under" ? "UNDER BUDGET" :
    status === "over"  ? "OVER BUDGET"  : "WITHIN 10% OF BUDGET";
  return (
    <div data-testid={testId} className={`p-5 border ${cls}`}>
      <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">{label}</div>
      <div className="font-heading font-bold text-3xl tabular-nums mt-3">
        {value >= 0 ? "+" : ""}{fmtMoney(value)}
      </div>
      <div className="text-[11px] mt-1 tracking-widest uppercase">{sublabel}</div>
    </div>
  );
}

function Bench({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono mt-1 ${highlight ? "text-[#00D4FF]" : ""}`}>{value}</div>
    </div>
  );
}

function BreakdownTable({ title, colLabel, keyField, rows, testId, accent }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="bg-[#12141D] border border-border overflow-x-auto" data-testid={testId}>
      <div className="p-5 pb-3 flex items-center gap-2">
        <BarChart3 size={16} style={{ color: accent }} />
        <h3 className="font-heading font-semibold">{title}</h3>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2">{rows.length} groups</span>
      </div>
      <table className="w-full text-left text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8] bg-[#1A1C28]">
          <tr>
            <th className="py-2.5 px-4">{colLabel}</th>
            <th className="py-2.5 px-4 text-right">Jobs</th>
            <th className="py-2.5 px-4 text-right">Share</th>
            <th className="py-2.5 px-4 text-right">SQFT</th>
            <th className="py-2.5 px-4 text-right">Hrs/SF</th>
            <th className="py-2.5 px-4 text-right">Lab/SF</th>
            <th className="py-2.5 px-4 text-right">Mat/SF</th>
            <th className="py-2.5 px-4 text-right">Total/SF</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className="border-t border-border" data-testid={`${testId}-row-${idx}`}>
              <td className="py-2.5 px-4 font-mono" style={{ color: accent }}>{r[keyField] || "—"}</td>
              <td className="py-2.5 px-4 font-mono text-right">{r.jobs}</td>
              <td className="py-2.5 px-4 font-mono text-right text-muted-foreground">{r.share_pct}%</td>
              <td className="py-2.5 px-4 font-mono text-right">{fmtNum(r.sqft)}</td>
              <td className="py-2.5 px-4 font-mono text-right">{r.hours_per_sqft}</td>
              <td className="py-2.5 px-4 font-mono text-right">{fmtMoney(r.labor_per_sqft)}</td>
              <td className="py-2.5 px-4 font-mono text-right">{fmtMoney(r.material_per_sqft)}</td>
              <td className="py-2.5 px-4 font-mono text-right text-[#00D4FF]">{fmtMoney(r.total_per_sqft)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
